import type { PermissionRequest, Session } from "@mux/sdk/v2/client"
import { sessionSidebarAt, sessionSnoozedUntil } from "./session-snooze"
import { isCanonicalOracleSession } from "@/context/oracle/session-cache"
import { cmp } from "./utils"
import { SESSION_RECENT_LIMIT, SESSION_RECENT_WINDOW } from "./types"

export function rootSessionLoadTarget(limit: number) {
  return Math.max(0, limit) * 2
}

function sessionUpdatedAt(session: Session) {
  return session.time.updated ?? session.time.created
}

export function sessionActivityAt(session: Session) {
  return session.time.activity ?? session.time.updated ?? session.time.created
}

export function compareSessionActivity(a: Session, b: Session) {
  const aActivity = sessionSidebarAt(a)
  const bActivity = sessionSidebarAt(b)
  if (aActivity !== bActivity) return bActivity - aActivity
  return cmp(b.id, a.id)
}

function compareSessionUpdated(a: Session, b: Session) {
  const aUpdated = sessionUpdatedAt(a)
  const bUpdated = sessionUpdatedAt(b)
  if (aUpdated !== bUpdated) return bUpdated - aUpdated
  return cmp(b.id, a.id)
}

function takeRecentSessions(sessions: Session[], limit: number, cutoff: number) {
  if (limit <= 0) return [] as Session[]
  const selected: Session[] = []
  const seen = new Set<string>()
  for (const session of sessions) {
    if (!session?.id) continue
    if (seen.has(session.id)) continue
    seen.add(session.id)
    if (sessionUpdatedAt(session) <= cutoff) continue
    const index = selected.findIndex((x) => compareSessionUpdated(session, x) < 0)
    if (index === -1) selected.push(session)
    if (index !== -1) selected.splice(index, 0, session)
    if (selected.length > limit) selected.pop()
  }
  return selected
}

export function trimSessions(
  input: Session[],
  options: { limit: number; permission: Record<string, PermissionRequest[]>; now?: number; pinned?: Set<string>; activeSessionIds?: Set<string> },
) {
  const limit = Math.max(0, options.limit)
  const cutoff = (options.now ?? Date.now()) - SESSION_RECENT_WINDOW
  // Canonical Oracles back workspace-header rows even when their chat is not
  // routed. Keep them outside the ordinary chat quota; other hidden work stays hidden.
  const oracles = input.filter((s) => !!s?.workspaceID && isCanonicalOracleSession(s, s.workspaceID) && !s.time?.archived)
  const oracleIDs = new Set(oracles.map((s) => s.id))
  const all = input
    .filter((s) => !!s?.id && !oracleIDs.has(s.id))
    .filter((s) => !s.hidden || options.activeSessionIds?.has(s.id))
    .sort((a, b) => compareSessionActivity(a, b))
  const archived = all.filter((s) => !!s.time?.archived || sessionSnoozedUntil(s) > 0)
  const active = all.filter((s) => !s.time?.archived && !sessionSnoozedUntil(s))

  const roots = active.filter((s) => !s.parentID)
  const children = active.filter((s) => !!s.parentID)
  const base = roots.slice(0, limit)
  const pinned = roots.slice(limit).filter((s) => options.pinned?.has(s.id) || options.activeSessionIds?.has(s.id))
  const recent = takeRecentSessions(roots.slice(limit), SESSION_RECENT_LIMIT, cutoff)
  // All three come from `roots`, so a repeated ID is the same session; a Map keeps its first position.
  const keepRoots = [...new Map([...base, ...pinned, ...recent].map((session) => [session.id, session])).values()]
  const keepRootIds = new Set([...oracleIDs, ...keepRoots.map((s) => s.id)])
  const keepChildren = children.filter((s) => {
    if (s.parentID && keepRootIds.has(s.parentID)) return true
    const perms = options.permission[s.id] ?? []
    if (perms.length > 0) return true
    return sessionUpdatedAt(s) > cutoff
  })
  return [...oracles, ...archived, ...keepRoots, ...keepChildren].sort((a, b) => cmp(a.id, b.id))
}
